// Piezas del ContentViewer de lecciones. Extraídas de content-viewer.tsx por
// ENG-LOC-01.
import { useEffect, useState } from 'react';
import { ExternalLink, RotateCw } from 'lucide-react';
import { useRefreshEnrollmentProgress } from '@/api/enrollments';

const PRIMARY =
  'inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90';
const SECONDARY =
  'inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent';

// Mensaje + botón para abrir el archivo en otra pestaña cuando no se puede
// mostrar inline (formato no previsualizable o imagen que no cargó). Con
// `onRetry`, reintentar es la acción principal y abrir en pestaña la secundaria.
export function ViewerFallback({
  url,
  message,
  onRetry,
}: {
  url: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        {onRetry && (
          <button type="button" onClick={onRetry} className={PRIMARY}>
            <RotateCw className="h-4 w-4" />
            Volver a cargar
          </button>
        )}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={onRetry ? SECONDARY : PRIMARY}
        >
          <ExternalLink className="h-4 w-4" />
          Abrir en nueva pestaña
        </a>
      </div>
    </div>
  );
}

// Foto de una lección de lectura/presentación. Va a su ancho natural (con tope
// en el ancho del lector) para que una página escaneada se lea con scroll y no
// quede encogida a la altura de la pantalla. `onLoaded` corre después de que el
// DOM ya refleja la imagen (sin el skeleton), para que la medición de "lectura
// completa" use la altura real.
export function LessonImage({
  url,
  title,
  onLoaded,
}: {
  url: string;
  title: string;
  onLoaded: () => void;
}) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const refreshProgress = useRefreshEnrollmentProgress();

  useEffect(() => {
    if (status === 'loaded') onLoaded();
  }, [status, onLoaded]);

  if (status === 'error') {
    // La causa más común es una URL firmada vencida (página abierta > 1 h):
    // refrescar el avance trae una URL nueva (remonta por key) y, si es la
    // misma, volver a 'loading' reintenta la descarga.
    return (
      <ViewerFallback
        url={url}
        message="No se pudo cargar la imagen: el enlace puede haber vencido. Vuelva a cargarla."
        onRetry={() => {
          setStatus('loading');
          void refreshProgress();
        }}
      />
    );
  }

  // El skeleton va superpuesto (absolute) para no sumar su alto al de la foto:
  // así la altura no salta al terminar de cargar.
  return (
    <div className="relative mx-auto max-w-4xl">
      {status === 'loading' && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 aspect-[4/3] animate-pulse rounded-lg bg-muted motion-reduce:animate-none"
        />
      )}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- onLoad/onError son eventos de carga del recurso, no interacción del usuario. */}
      <img
        src={url}
        alt={title}
        draggable={false}
        onLoad={() => setStatus('loaded')}
        onError={() => setStatus('error')}
        className={`mx-auto block h-auto max-w-full rounded-lg transition-opacity duration-200 motion-reduce:transition-none ${
          status === 'loaded' ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
}
